import { t } from '@lingui/macro';
import { StandardTextfield, StandardQuantity, FontSizes } from '@brandfolder/react';
import React, { ChangeEventHandler, FunctionComponent, useState, useEffect } from 'react';

import { LimitTypes } from './customLimitsTypes';
import { assignLimitsUnits } from './helpers/assignLimitsUnits';

export const USAGE_ERROR_TEXT = (): string => t`Must match or exceed current usage`;

interface LimitsTableRowInputProps {
  currentLimit: number;
  currentUsage: number;
  handleChange: (value: number, resourceId: string) => void;
  limitType: LimitTypes;
  resourceId: string;
  onErrorChange?: (hasError: boolean, resourceId: string, limitType: LimitTypes) => void;
}

export const LimitsTableRowInput: FunctionComponent<LimitsTableRowInputProps> =
  ({ resourceId, currentLimit, currentUsage, handleChange, limitType, onErrorChange }) => {
    const [localLimit, setLocalLimit] = useState(currentLimit);

    useEffect(() => {
      setLocalLimit(currentLimit);
    }, [currentLimit]);

    const onCurrentLimitChange: ChangeEventHandler<HTMLInputElement> = (
      e
    ): void => {
      const { value } = e.target;
      const numericValue = parseInt(value) || 0;
      setLocalLimit(numericValue);
      handleChange(numericValue, resourceId);
    };

    const onQuantityChange = (limit: number): void => {
      handleChange(limit, resourceId);
    };

    // Use local value for error calculation to provide immediate feedback
    const hasError = localLimit < currentUsage;
    const minLimit: number = currentLimit < currentUsage ? 0 : currentUsage;

    // Notify parent component when error state changes
    useEffect(() => {
      if (onErrorChange) {
        onErrorChange(hasError, resourceId, limitType);
      }
    }, [hasError, resourceId, limitType, onErrorChange]);

    return limitType === 'admin' || limitType === 'collaborator' || limitType === 'custom' ? (
      <StandardQuantity
        data-testid="limits-standard-quantity"
        defaultValue={currentLimit}
        error={hasError && USAGE_ERROR_TEXT()}
        errorProps={
          hasError && {
            size: FontSizes.XxSmall,
            children: USAGE_ERROR_TEXT(),
            id: resourceId,
          }
        }
        id={`${resourceId}--currentLimit`}
        key={`${resourceId}-${currentLimit}`}
        labels={{
          decrementLabel: t`Decrement limit`,
          incrementLabel: t`Increment limit`,
          label: t`Admin Limit`,
        }}
        min={minLimit}
        onQuantityChange={onQuantityChange}
        showLabel={false}
      />
    ) : (
      <div className={`limits-textfield-wrapper ${limitType}`}>
        <StandardTextfield
          className={`${limitType}-input`}
          data-testid="limits-standard-text-field"
          error={hasError && USAGE_ERROR_TEXT()}
          errorProps={
            hasError && {
              size: FontSizes.XxSmall,
              children: USAGE_ERROR_TEXT(),
              id: resourceId,
            }
          }
          id={`${resourceId}--currentLimit`}
          label={t`Limit Type Input`}
          min={minLimit}
          onChange={onCurrentLimitChange}
          showLabel={false}
          type="number"
          value={localLimit || ''}
        />
        <span className={'limits-textfield-input__units'}>
          {assignLimitsUnits(limitType, '')}
        </span>
      </div>
    );
  };
